/**
 * Employer "Record leave" drawer — submits a leave request on behalf of an
 * employee via `payroll.requestLeave`. Used from `leave_screen.tsx`.
 *
 * ── Form state ──────────────────────────────────────────────────────────────
 *
 * - Employee: `StyledSelect` over the caller-supplied employee list.
 * - Leave type: `StyledSelect` over `listLeaveTypes` fetched here (needs the
 *   entity's active types, including casual, which the old hardcoded enum
 *   missed — see #wagepro-leave-phase2).
 * - Start / end: `DateInput` pair.
 * - Days: auto-derived by `count_weekdays()` (business days Mon–Fri); can be
 *   overridden via `AutoGrowTextarea numeric`. Stored ×100 (e.g. "3" → 300)
 *   to match the backend's `days_requested` Zod schema.
 * - Notes: `AutoGrowTextarea` (optional, max 2000 chars).
 *
 * ── Failure classification ──────────────────────────────────────────────────
 *
 * `overlap` and `unknown_leave_type` are rendered as inline field errors
 * (not toasts) because they are actionable without leaving the drawer.
 * All other failures surface as a red `<Alert>` at the bottom.
 */
import * as React from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Alert,
  AutoGrowTextarea,
  Button,
  DateInput,
  Drawer,
  EmptyState,
  StyledSelect,
  type StyledSelectOption,
} from '@jumpstone/ui-react';
import { useLeaveTypes } from '../../data/use_leave_types';
import type {
  LeaveRequestFailure,
  LeaveRequestResult,
  RecordLeaveInput,
} from '../../data/use_leave_requests';
import { count_weekdays, leave_record_error_message } from './leave_utils';
import {
  leave_eligible_employees,
  type LeaveEmployeeLookup,
} from '../../data/wagepro_store_leave';

/** Alias — the caller already has this type from wagepro_store_leave. */
export type { LeaveEmployeeLookup };

export interface LeaveRecordDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employees: readonly LeaveEmployeeLookup[];
  /** Called when the mutation succeeds — parent re-fetches the queue. */
  onSuccess: () => void;
  /** The actual mutation — passed in from `useLeaveRequests` so the drawer has no direct hook dep. */
  request: (input: RecordLeaveInput) => Promise<LeaveRequestResult>;
}

function today_iso(): string {
  return new Date().toISOString().slice(0, 10);
}

interface FormState {
  employee_id: string;
  leave_type_code: string;
  start_date: string;
  end_date: string;
  days_raw: string;
  days_override: boolean;
  notes: string;
}

const EMPTY_FORM: FormState = {
  employee_id: '',
  leave_type_code: '',
  start_date: today_iso(),
  end_date: today_iso(),
  days_raw: '1',
  days_override: false,
  notes: '',
};

/** Inline field errors rendered beneath the affected field. */
interface FieldErrors {
  employee?: string;
  leave_type?: string;
  days?: string;
  dates?: string;
  overlap?: string;
}

export function LeaveRecordDrawer({
  open,
  onOpenChange,
  employees,
  onSuccess,
  request,
}: LeaveRecordDrawerProps): React.JSX.Element {
  const { t } = useTranslation('wp');
  const { leave_types, state: leave_types_state } = useLeaveTypes();

  const [form, set_form] = React.useState<FormState>(EMPTY_FORM);
  const [field_errors, set_field_errors] = React.useState<FieldErrors>({});
  const [submit_failure, set_submit_failure] =
    React.useState<LeaveRequestFailure | null>(null);
  const [is_submitting, set_is_submitting] = React.useState(false);

  // Reset on close
  React.useEffect(() => {
    if (!open) {
      set_form({
        ...EMPTY_FORM,
        start_date: today_iso(),
        end_date: today_iso(),
      });
      set_field_errors({});
      set_submit_failure(null);
      set_is_submitting(false);
    }
  }, [open]);

  // Auto-derive days from date range when user hasn't manually overridden
  React.useEffect(() => {
    if (!form.days_override) {
      const derived = count_weekdays(form.start_date, form.end_date);
      set_form((f) => ({
        ...f,
        days_raw: derived > 0 ? String(derived) : '1',
      }));
    }
  }, [form.start_date, form.end_date, form.days_override]);

  // Contractors accrue no leave, so they are never offered here.
  const employee_options: StyledSelectOption[] = leave_eligible_employees(
    employees,
  ).map((emp) => ({
    value: emp.id,
    label: emp.full_name,
  }));

  const leave_type_options: StyledSelectOption[] = leave_types.map((lt) => ({
    value: lt.code,
    label: lt.name,
  }));

  function validate(): boolean {
    const errors: FieldErrors = {};
    if (!form.employee_id) {
      errors.employee = t('leave.record_field_employee_required');
    }
    if (!form.leave_type_code) {
      errors.leave_type = t('leave.record_field_leave_type_required');
    }
    if (!form.start_date || !form.end_date) {
      errors.dates = t('leave.record_field_dates_required');
    } else if (form.end_date < form.start_date) {
      errors.dates = t('leave.record_error_invalid_dates');
    }
    const days_float = parseFloat(form.days_raw);
    if (isNaN(days_float) || days_float <= 0) {
      errors.days = t('leave.record_field_days_invalid');
    }
    set_field_errors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handle_submit(): Promise<void> {
    set_submit_failure(null);
    if (!validate()) return;

    const days_float = parseFloat(form.days_raw);
    // Scaled ×100 per docs/money-rules.md convention for day-counts
    const days_scaled = Math.round(days_float * 100);

    const input: RecordLeaveInput = {
      employee_id: form.employee_id,
      leave_type: form.leave_type_code,
      start_date: form.start_date,
      end_date: form.end_date,
      days_requested: days_scaled,
      ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
    };

    set_is_submitting(true);
    const result = await request(input);
    set_is_submitting(false);

    if (result.outcome === 'failure') {
      if (result.failure === 'overlap') {
        set_field_errors((e) => ({
          ...e,
          overlap: leave_record_error_message(t, 'overlap'),
        }));
        return;
      }
      if (result.failure === 'unknown_leave_type') {
        set_field_errors((e) => ({
          ...e,
          leave_type: leave_record_error_message(t, 'unknown_leave_type'),
        }));
        return;
      }
      if (result.failure === 'invalid_dates') {
        set_field_errors((e) => ({
          ...e,
          dates: leave_record_error_message(t, 'invalid_dates'),
        }));
        return;
      }
      set_submit_failure(result.failure);
      return;
    }

    onSuccess();
    onOpenChange(false);
  }

  const leave_types_loading = leave_types_state.status === 'loading';
  const leave_types_error =
    leave_types_state.status === 'entitlement_required' ||
    leave_types_state.status === 'error';

  return (
    <Drawer
      open={open}
      onClose={() => onOpenChange(false)}
      title={t('leave.record_drawer_title')}
    >
      <div className="flex flex-col gap-4 p-1">
        {/* Employee picker */}
        <label className="flex flex-col gap-1">
          <span className="nv-label">{t('leave.record_field_employee')}</span>
          <StyledSelect
            value={form.employee_id}
            options={employee_options}
            placeholder={t('leave.record_field_employee_placeholder')}
            onChange={(next) => {
              set_form((f) => ({ ...f, employee_id: next }));
              set_field_errors((e) => ({ ...e, employee: undefined }));
            }}
          />
          {field_errors.employee !== undefined && (
            <p className="nv-help is-error">{field_errors.employee}</p>
          )}
        </label>

        {/* Leave type picker */}
        <label className="flex flex-col gap-1">
          <span className="nv-label">{t('leave.record_field_leave_type')}</span>
          {leave_types_loading && (
            <div className="nv-skel" style={{ height: 36 }} />
          )}
          {leave_types_error && (
            <p className="nv-help is-error">
              {t('leave.record_field_leave_type_load_error')}
            </p>
          )}
          {!leave_types_loading && !leave_types_error && (
            <StyledSelect
              value={form.leave_type_code}
              options={leave_type_options}
              placeholder={t('leave.record_field_leave_type_placeholder')}
              onChange={(next) => {
                set_form((f) => ({ ...f, leave_type_code: next }));
                set_field_errors((e) => ({
                  ...e,
                  leave_type: undefined,
                  overlap: undefined,
                }));
              }}
            />
          )}
          {field_errors.leave_type !== undefined && (
            <p className="nv-help is-error">{field_errors.leave_type}</p>
          )}
        </label>

        {/* Date range */}
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1">
            <span className="nv-label">
              {t('leave.record_field_start_date')}
            </span>
            <DateInput
              value={form.start_date}
              onChange={(e) => {
                set_form((f) => ({
                  ...f,
                  start_date: e.target.value,
                  days_override: false,
                }));
                set_field_errors((e2) => ({
                  ...e2,
                  dates: undefined,
                  overlap: undefined,
                }));
              }}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="nv-label">{t('leave.record_field_end_date')}</span>
            <DateInput
              value={form.end_date}
              onChange={(e) => {
                set_form((f) => ({
                  ...f,
                  end_date: e.target.value,
                  days_override: false,
                }));
                set_field_errors((e2) => ({
                  ...e2,
                  dates: undefined,
                  overlap: undefined,
                }));
              }}
            />
          </label>
        </div>
        {field_errors.dates !== undefined && (
          <p className="nv-help is-error -mt-2">{field_errors.dates}</p>
        )}

        {/* Days — auto-derived but overridable */}
        <label className="flex flex-col gap-1">
          <span className="nv-label">{t('leave.record_field_days')}</span>
          <div className="max-w-[120px]">
            <AutoGrowTextarea
              numeric
              value={form.days_raw}
              size="md"
              onChange={(e) => {
                set_form((f) => ({
                  ...f,
                  days_raw: e.target.value,
                  days_override: true,
                }));
                set_field_errors((e2) => ({ ...e2, days: undefined }));
              }}
            />
          </div>
          <p className="nv-help">{t('leave.record_field_days_hint')}</p>
          {field_errors.days !== undefined && (
            <p className="nv-help is-error">{field_errors.days}</p>
          )}
        </label>

        {/* Notes */}
        <label className="flex flex-col gap-1">
          <span className="nv-label">{t('leave.record_field_notes')}</span>
          <AutoGrowTextarea
            value={form.notes}
            size="md"
            placeholder={t('leave.record_field_notes_placeholder')}
            onChange={(e) => {
              set_form((f) => ({ ...f, notes: e.target.value }));
            }}
          />
        </label>

        {/* Overlap inline error */}
        {field_errors.overlap !== undefined && (
          <Alert
            variant="warning"
            title={t('leave.record_error_overlap_title')}
          >
            {field_errors.overlap}
          </Alert>
        )}

        {/* Generic submission failure */}
        {submit_failure !== null && (
          <Alert variant="danger" title={t('leave.record_error_title')}>
            {leave_record_error_message(t, submit_failure)}
          </Alert>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={is_submitting || leave_types_loading || leave_types_error}
            onClick={() => {
              void handle_submit();
            }}
          >
            {is_submitting
              ? t('leave.record_submitting')
              : t('leave.record_submit')}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}

/** Empty state rendered inside the drawer when leave_types_state is entitlement_required. */
export function LeaveTypesEntitlementRequired(): React.JSX.Element {
  const { t } = useTranslation('wp');
  return (
    <EmptyState
      title={t('leave.entitlement_required_title')}
      description={t('leave.entitlement_required_description')}
    />
  );
}
